import produce from 'immer';
import createCachedSelector from 're-reselect';
import { combineReducers } from 'redux';
import { createSelector } from 'reselect';
import { getType } from 'typesafe-actions';
import { actions } from '..';
import { Sentiment } from '../../api/transcriptions';
import { CallBookmark, Transcription, TranscriptionParagraph } from '../../model';
import { Actions, StoreState } from '../types';

interface Transcriptions {
  [id: string]: Transcription | null
}

interface Tags {
  [id: string]: CallBookmark[]
}

function transcriptions(state: Transcriptions = {}, action: Actions): Transcriptions {
  switch (action.type) {
    case getType(actions.fetchCallTranscriptionRequest):
      {
        const { mediaId } = action.payload

        return {
          ...state,
          [mediaId]: null,
        }
      }

    case getType(actions.fetchCallTranscriptionResponse):
      {
        const { mediaId, data } = action.payload

        const transcripiton = state[mediaId]

        if (transcripiton) {
          return {
            ...state,
            [mediaId]: {
              ...transcripiton,
              paragraphs: [...transcripiton.paragraphs, ...data.paragraphs],
            },
          }
        } else {
          return {
            ...state,
            [mediaId]: data,
          }
        }
      }

    case getType(actions.fetchCallTranscriptionCallResponse):
      {
        const { mediaId, data } = action.payload
        const transcripiton = state[mediaId]

        if (!transcripiton) return state
        const calls = transcripiton.calls.filter(x => x.callId !== data.callId)

        return {
          ...state,
          [mediaId]: {
            ...transcripiton,
            calls: [...calls, data],
          },
        }
      }
    case getType(actions.notifyTranscriptionStarted):
      {
        const { mediaId, calls } = action.payload
        const transcripiton = state[mediaId]

        if (!transcripiton) {
          return {
            ...state,
            [mediaId]: {
              calls,
              paragraphs: [],
              sentiment: Sentiment.none, neutral: 0, negative: 0, positive: 0,
            },
          }
        }

        return {
          ...state,
          [mediaId]: {
            ...transcripiton,
            calls,
          },
        }
      }

    case getType(actions.notifyTranscriptionParagraphUpdated):
      {
        const { mediaId, paragraphId, callId, createdAtUtc, modifiedAtUtc, modifiedBy, text, canEdit, call } = action.payload
        const transcripiton = state[mediaId]

        if (!transcripiton) return state

        const p = transcripiton.paragraphs.find(x => x.paragraphId === paragraphId)
        const paragraphs = transcripiton.paragraphs.filter(x => x.paragraphId !== paragraphId)

        const paragraph: TranscriptionParagraph = p !== undefined ? {
          ...p,
          callId: callId,
          createdAtUtc: createdAtUtc,
          modifiedAtUtc: modifiedAtUtc,
          modifiedBy: modifiedBy,
          text: text,
          canEdit: canEdit,
          original: !p.original ? text : p.original,
        } : {
            paragraphId: paragraphId,
            callId: callId,
            createdAtUtc: createdAtUtc,
            modifiedAtUtc: modifiedAtUtc,
            modifiedBy: modifiedBy,
            text: text,
            canEdit: canEdit,
            contactId: call ? call.contactId : undefined,
            displayName: call ? call.displayName : undefined,
            extensionId: call ? call.extensionId : undefined,
            telephoneNumber: call ? call.telephoneNumber : undefined,
            original: undefined,
            sentiment: 0, negative: 0, neutral: 0, positive: 0,
          }


        return {
          ...state,
          [mediaId]: {
            ...transcripiton,
            paragraphs: [...paragraphs, { ...paragraph }].sort((a, b) => a.createdAtUtc >= b.createdAtUtc ? 1 : -1),
          },
        }
      }

    case getType(actions.requestTranscriptionParagraphOrginal):
      {
        const { mediaId, paragraphId, text } = action.payload
        const transcripiton = state[mediaId]

        if (!transcripiton) return state

        const p = transcripiton.paragraphs.find(x => x.paragraphId === paragraphId)
        if (!p || p.original) return state

        const paragraphs = transcripiton.paragraphs.filter(x => x.paragraphId !== paragraphId)

        return {
          ...state,
          [mediaId]: {
            ...transcripiton,
            paragraphs: [...paragraphs, { ...p, original: text }].sort((a, b) => a.createdAtUtc >= b.createdAtUtc ? 1 : -1),
          },
        }
      }

    case getType(actions.notifySentimentParagraph):
      {
        const { mediaId, paragraphId, sentiment, modifiedBy, modifiedAtUtc, neutral, negative, positive } = action.payload
        const transcripiton = state[mediaId]

        if (!transcripiton) return state

        const p = transcripiton.paragraphs.find(x => x.paragraphId === paragraphId)
        if (!p) return state

        const paragraphs = transcripiton.paragraphs.filter(x => x.paragraphId !== paragraphId)
        const x = p.modifiedBy && !modifiedBy ? p.modifiedBy : modifiedBy

        return {
          ...state,
          [mediaId]: {
            ...transcripiton,
            paragraphs: [...paragraphs, { ...p, sentiment, modifiedBy: x, modifiedAtUtc, neutral, negative, positive }].sort((a, b) => a.createdAtUtc >= b.createdAtUtc ? 1 : -1),
          },
        }
      }

    case getType(actions.notifySentimentCall):
      {
        const { mediaId, sentiment, neutral, negative, positive } = action.payload
        const transcripiton = state[mediaId]

        if (!transcripiton) return state

        return {
          ...state,
          [mediaId]: {
            ...transcripiton, sentiment, neutral, negative, positive,
          },
        }
      }

    case getType(actions.removeMediaItem): {
      const newState = { ...state }
      delete newState[action.payload]
      return newState
    }
    default:
      return state
  }
}

function tags(state: Tags = {}, action: Actions): Tags {
  switch (action.type) {
    case getType(actions.createCallTranscriptionTag):
      {
        return produce(state, state => {
          const { mediaId, bookmarkId, title, timeStamp, bookmarkTypeId, createdBy, createdAt, modifiedBy, modifiedAt } = action.payload

          if (!state[mediaId]) {
            state[mediaId] = []
          }

          const data = state[mediaId]
          const t = { bookmarkId, title, timeStamp, bookmarkTypeId, createdBy, createdAt, modifiedBy, modifiedAt }

          const x = data.filter(x => x.bookmarkId !== t.bookmarkId)
          x.push(t)
          x.sort((a, b) => a.createdAt > b.createdAt ? 1 : -1)
          state[mediaId] = x
        })
      }
    case getType(actions.renamedCallTranscriptionTag):
      {
        return produce(state, state => {
          const { mediaId, bookmarkId, title, modifiedBy, modifiedAt } = action.payload

          if (!state[mediaId]) {
            state[mediaId] = []
          }

          const data = state[mediaId]
          const tag = data.find(x => x.bookmarkId === bookmarkId)
          if (!tag) return state

          const x = data.filter(x => x.bookmarkId !== bookmarkId)
          x.push({ ...tag, title, modifiedBy, modifiedAt })
          x.sort((a, b) => a.createdAt > b.createdAt ? 1 : -1)
          state[mediaId] = x
        })
      }
    case getType(actions.movedCallTranscriptionTag):
      {
        return produce(state, state => {
          const { mediaId, bookmarkId, timeStamp, modifiedBy, modifiedAt } = action.payload

          if (!state[mediaId]) {
            state[mediaId] = []
          }

          const data = state[mediaId]
          const tag = data.find(x => x.bookmarkId === bookmarkId)
          if (!tag) return state

          const x = data.filter(x => x.bookmarkId !== bookmarkId)
          x.push({ ...tag, timeStamp, modifiedBy, modifiedAt })
          x.sort((a, b) => a.createdAt > b.createdAt ? 1 : -1)
          state[mediaId] = x
        })
      }
    case getType(actions.removeMediaItem): {
      const newState = { ...state }
      delete newState[action.payload]
      return newState
    }

    case getType(actions.deleteCallTranscriptionTag):
      {
        return produce(state, state => {
          const { mediaId, tagId } = action.payload

          if (!state[mediaId]) {
            return
          }

          const data = state[mediaId]
          const x = data.filter(x => x.bookmarkId !== tagId)
          state[mediaId] = x
        })
      }

    case getType(actions.fetchCallTranscriptionTagsResponse):
      {
        const { mediaId, tags } = action.payload


        return {
          ...state,
          [mediaId]: tags.sort((a, b) => a.createdAt > b.createdAt ? 1 : -1),
        }
      }

    default:
      return state
  }
}

export default combineReducers({
  transcriptions,
  tags,
})

function getRoot(state: StoreState) { return state.callTranscriptions }

export function getTranscriptions(state: StoreState, id: string) {
  return getRoot(state).transcriptions[id]
}

export function getTags(state: StoreState, id: string) {
  return getRoot(state).tags[id]
}

interface SelectParagraphArgs {
  mediaId: string
  paragraphId: string
}

export const transcriptionLoaded = createSelector(
  (s: StoreState, medaId: string) => getTranscriptions(s, medaId),
  (transcription) => {
    return transcription !== undefined
  },
)

export const hasTranscription = createSelector(
  (s: StoreState, medaId: string) => getTranscriptions(s, medaId),
  (transcription) => {
    if (transcription !== undefined && transcription !== null && transcription.paragraphs.length > 0) {
      return true
    }
    return false
  },
)

export const selectTranscriptionParagraph = createCachedSelector(
  (s: StoreState, { mediaId }: SelectParagraphArgs) => {
    const transcription = getTranscriptions(s, mediaId)
    return transcription
  },
  (s: StoreState, { paragraphId }: SelectParagraphArgs) => paragraphId,
  (transcription, paragraphId) => {
    if (!transcription || !transcription.paragraphs) return undefined
    return transcription.paragraphs.find(x => x.paragraphId === paragraphId)
  })(
    (_, medaId, paragraphId) => medaId + paragraphId,
  )

export const selectTranscription = createCachedSelector(
  (s: StoreState, medaId: string) => getTranscriptions(s, medaId),
  (s: StoreState, medaId: string) => getTags(s, medaId),
  (transcription, tags) => {

    if (transcription) {
      let temp: TranscriptionParagraph[] = []
      for (let index = 0; index < transcription.paragraphs.length; index++) {
        const element = transcription.paragraphs[index];
        const next = index + 1 < transcription.paragraphs.length ? transcription.paragraphs[index + 1].createdAtUtc : Number.MAX_SAFE_INTEGER
        const t = tags && tags.filter(t => t.timeStamp >= element.createdAtUtc && t.timeStamp < next)
        temp.unshift({ ...element, tags: t })
      }
      return temp.sort((a, b) => a.createdAtUtc >= b.createdAtUtc ? 1 : -1)
    }

    return undefined
  },
)(
  (_, medaId) => medaId,
)

export const selectTranscriptionScore = createCachedSelector(
  (s: StoreState, medaId: string) => getTranscriptions(s, medaId),
  (transcription) => {

    if (transcription) {
      return {
        score: transcription.sentiment,
        neutral: transcription.neutral,
        negative: transcription.negative,
        positive: transcription.positive,
      }
    }

    return {
      score: Sentiment.none,
      neutral: 0,
      negative: 0,
      positive: 0,
    }
  },
)(
  (_, medaId) => medaId,
)
